import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { Button, Col, Form, Row, Spinner } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import Search from "../../../../components/Search";
import { SelectedFilter } from "../../../../components/Filters/types";
import { setCurrentPage, setSearchText } from "../../../../redux/historicalSlice";
import { setFilterByOnlyActiveRecords } from "../../../../redux/filtersSlice";
import { RootState } from "../../../../redux/store";
import { deleteLogicRole, useRoles } from "../api/roleAPI";
import AppTable, { ColumnConfig } from "../../../../components/AppTable/AppTable";
import { RoleListItemDto } from "../types";
import { SectionPermissionTypeEnum } from "../../../../types/SectionPermissionTypesEnum";
import { useIsSuperAdmin, useLanguageUser, useUserPermissions } from "../../../../utils/auth";
import { PERMISSIONS } from "../../../../constants/permissions";
import { useLocation, useNavigate } from "react-router-dom";
import { formatDate } from "../../../../utils/formatters/DateFormatter";
import { useModal } from "../../../../hooks/useModal";
import ModalGlobal from "../../../../components/AppModal/ModalGlobal";
import { languageMap } from "../../../../utils/formatters/UserLanguageToDescriptionField";
import AlertMessage from "../../../../components/AlertMessage";
import { useAlert } from "../../../../hooks/useAlert";

export default function RolesList() {
    const { t } = useTranslation(["role", "common"]);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const language = useLanguageUser();
    const location = useLocation();

    const permissions = useUserPermissions();

    const currentPage = useSelector((state: RootState) => state.historical.roles.currentPage);
    const itemsPerPage = useSelector((state: RootState) => state.historical.roles.itemsPerPage);
    const searchText = useSelector((state: RootState) => state.historical.roles.searchText);

    const onlyActiveToggle = useSelector((state: RootState) => state.filters.roles.filterByOnlyActiveRecords);

    const [searchTerm, setSearchTerm] = useState("");
    const [appliedFilters] = useState<SelectedFilter[]>([]);

    const { data: rolesPagination, isLoading, error, refetch 
    } = useRoles({
        search: searchTerm,
        filters: appliedFilters,
    });

    useEffect(() => {
        if (location.state?.shouldRefresh) {
            refetch();
        }
    }, [location.state, refetch]);

    const handleSearchInput = () => {
        setSearchTerm(searchText || "");
        dispatch(setCurrentPage({ table: "roles", page: 1 }));
    };

    const handleClear = () => {
        dispatch(setCurrentPage({ table: "roles", page: 1 }));
        dispatch(setSearchText({ table: "roles", text: "" }));
        setSearchTerm("");
    };

    const columns: ColumnConfig<RoleListItemDto>[] = [
        {
            header: "",
            key: "id",
            render: (item) => (
                <span style={{ display: "inline-flex", alignItems: "center" }}>
                    {item.clientId == null && (
                        <i className="bi bi-lock-fill" style={{ color: "grey", marginLeft: 4 }} />
                    )}
                </span>
            )
        },
        { header: t("role:table.id"), key: "id", render: (item) => <b>{item.id}</b> },
        {
            header: t("role:table.description"), key: "description"
        },
        { header: t("role:table.publishDate"), key: "validityDate", render: (item) => <>{formatDate(item.validityDate)}</> },
        {
            header: t("role:table.status"),
            key: "status",
            render: (item) => (
                <span
                    className={`status-tag ${item.deletedDate == null ? "status-tag--active" : "status-tag--disabled"}`}
                >
                    {item.deletedDate == null
                        ? t("role:table.statusActive")
                        : t("role:table.statusDisable")}
                </span>
            )
        },
    ];


    const { showModal, modalData, endDate, openModal: openDeleteModal, closeModal, setLoading, currentItem } = useModal<RoleListItemDto>();
    const { alert, hideAlert, isVisible } = useAlert({ fromLocation: true });

    const ROLE_MODAL_HEADERS = {
        titleDelete: t("role:modal.rolModalHeaderDeleteTitle"),
        subtitleDelete: t("role:modal.rolModalHeaderDeleteSubtitle"),
    };

    const handleDelete = async (role: RoleListItemDto) => {
        openDeleteModal(
            role,
            ROLE_MODAL_HEADERS.titleDelete,
            `${ROLE_MODAL_HEADERS.subtitleDelete} ${ role.description?.[languageMap[language]] ?? role.description?.catalan ?? role.description}?`,
            "deleteWithoutInput",
            ""
        );
    };

    const handleToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
        dispatch(setFilterByOnlyActiveRecords({ table: "roles", filterByOnlyActiveRecords: e.target.checked }));
    };

    const confirmSubmitModal = async () => {
        setLoading(true); // Iniciar loading
 
        try {
            if (modalData.type === 'deleteWithoutInput') {
                await deleteLogicRole(currentItem?.id ?? 0);
            }
            closeModal();
            navigate('/admin/roles', {
                state: { message: t("role:modal.messageDeletedRoleSuccessfully"), shouldRefresh: true }
            });
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false); // Finalizar loading
        }
    }

    const isSuperAdmin = useIsSuperAdmin(); 

    return (
        <>
            <ModalGlobal
                show={showModal}
                onClose={closeModal}
                title={modalData.title}
                contentType={modalData.type}
                subtitle={modalData.subtitle}
                subtitle2={modalData.subtitle2}
                saveText={t("common:modalActionOK")}
                closeText={t("common:modalActionNOTOK")}
                endDate={endDate}
                onSave={() => confirmSubmitModal()}
                isLoading={isLoading}
            />
            <Row className="mb-2 d-flex align-items-center justify-content-between">
                <Col xs="auto">
                    <h2>{t("role:title")}</h2>
                </Col>

                {permissions.length > 0 && permissions.includes(PERMISSIONS.ROLES.CREATE) && (
                    <Col xs="auto">
                        <Button
                            variant="primary"
                            style={{ whiteSpace: "nowrap" }}
                            onClick={() => navigate(`/admin/roles/new-role`)}
                        >
                            {t("role:buttonNewRole")}
                        </Button>
                    </Col>
                )}
            </Row>

            <Row>
                <Col className="text-center" xs={12} md={12}>
                    {error && (
                        <div className="alert alert-danger">{t("common:requestError.errorBadRequest")}</div>
                    )}
                </Col>
            </Row>
            <Row>
                <Col className="text-center" xs={12} md={12}>
                    {isVisible && (
                        <AlertMessage
                            message={alert!.message}
                            type={alert!.type}
                            onClose={hideAlert}
                        />
                    )}
                </Col>
            </Row>
            <Row>
                <Col xs={12} md={12}>
                    <Search
                        searchText={searchText || ""}
                        onChange={(text) => dispatch(setSearchText({ table: "roles", text }))}
                        onSearch={handleSearchInput}
                        onClear={handleClear}
                    />
                </Col>
            </Row>
            

            <Row>
                <Form>
                    <Form.Check
                        className="my-4"
                        type="switch"
                        id="custom-switch"
                        label={t("common:buttons.toggleActiveRecords")}
                        onChange={handleToggle}
                        checked={onlyActiveToggle}
                    />
                </Form>
            </Row>

            <AppTable<RoleListItemDto>
                data={rolesPagination?.items || []}
                columns={columns}
                emptyMessage={"No records found"}
                t={t}
                hasActions={true}
                hasPagination={true}
                currentPage={currentPage}
                totalPages={rolesPagination?.totalPages || 0}
                pageSize={itemsPerPage}
                paginationType="roles"
                sectionPermission={SectionPermissionTypeEnum.ROLES}
                onView={(id:number) => {navigate(`/admin/roles/${id}`) }}
                canView={(role) => (isSuperAdmin || role.clientId != null) && role.deletedDate == null}
                onExportCsv={() => { }}
                onDelete={handleDelete}
                canDelete={(role) => (isSuperAdmin || role.clientId != null) && role.deletedDate == null}
            />

            {isLoading && (
                <Row>
                    <Col xs={12} className="text-center mt-2">
                        <Spinner animation="border" />
                    </Col>
                </Row>
            )}
        </>
    );
}
